import { Reveal, Eyebrow } from "./Reveal";
import { IMAGES, SITE } from "@/lib/site";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const cards = [
  {
    key: "luxury-watch",
    title: "Sell Your Luxury Watch",
    desc: "Contact our team at WeBuySwissWatches for the best deals on your luxury watch — Rolex, Omega & more.",
    img: IMAGES.watchLuxury,
    alt: "Luxury gold timepiece",
    href: SITE.watchBuyersUrl,
    external: true,
  },
  {
    key: "gold-jewelry",
    title: "Sell Your Gold Jewelry",
    desc: "Chains, rings, coins and estate pieces in 10K–22K — tested and weighed in front of you, competitive offer paid on the spot.",
    img: IMAGES.jewelry,
    alt: "Gold jewelry",
    href: "/sell-gold",
  },
  {
    key: "diamond-jewelry",
    title: "Sell Your Diamond Jewelry",
    desc: "Engagement rings, loose stones and tennis bracelets — graded by our diamond specialists with a fair, transparent offer.",
    img: IMAGES.diamond,
    alt: "Diamond ring",
    href: "/sell-diamonds",
  },
];

const watchBrands = [
  "Rolex", "Omega", "Cartier", "TAG Heuer", "Breitling", "Patek Philippe",
  "Tudor", "Seiko", "Citizen", "Bulova", "Fossil", "Michael Kors",
];

export const Showcase = () => {
  return (
    <section id="we-buy" data-testid="we-buy-section" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <Reveal className="max-w-2xl">
          <Eyebrow>The other side of the counter</Eyebrow>
          <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight text-neutral-900" data-testid="we-buy-heading">
            We buy. <span className="text-[#C9A227]">You sell.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-neutral-600">
            Gold, diamonds, and luxury watches — bring them to a Texas showroom and watch every
            test and weigh-in happen in front of you. Honest offers, paid on the spot.
          </p>
        </Reveal>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {cards.map((c, i) => (
            <Reveal key={c.key} delay={0.08 * i}>
              {c.external ? (
                <a
                  href={c.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`we-buy-card-${c.key}`}
                  className="group block h-full bg-white border border-[#E8DCC4] rounded-sm overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(184,134,11,0.10)] transition-shadow duration-500"
                >
                  <div className="overflow-hidden">
                    <img
                      src={c.img}
                      alt={c.alt}
                      className="w-full h-[300px] lg:h-[340px] object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    />
                  </div>
                  <div className="p-6 lg:p-8 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display text-2xl text-neutral-900">{c.title}</h3>
                      <p className="text-sm text-neutral-600 mt-2 leading-relaxed max-w-md">{c.desc}</p>
                    </div>
                    <span className="shrink-0 w-10 h-10 rounded-full border border-[#E8DCC4] flex items-center justify-center text-[#C9A227] group-hover:bg-[#C9A227] group-hover:text-neutral-900 transition-colors duration-300">
                      <ArrowUpRight size={18} strokeWidth={1.5} />
                    </span>
                  </div>
                </a>
              ) : (
                <Link
                  to={c.href}
                  data-testid={`we-buy-card-${c.key}`}
                  className="group block h-full bg-white border border-[#E8DCC4] rounded-sm overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(184,134,11,0.10)] transition-shadow duration-500"
                >
                  <div className="overflow-hidden">
                    <img
                      src={c.img}
                      alt={c.alt}
                      className="w-full h-[300px] lg:h-[340px] object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    />
                  </div>
                  <div className="p-6 lg:p-8 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display text-2xl text-neutral-900">{c.title}</h3>
                      <p className="text-sm text-neutral-600 mt-2 leading-relaxed max-w-md">{c.desc}</p>
                    </div>
                    <span className="shrink-0 w-10 h-10 rounded-full border border-[#E8DCC4] flex items-center justify-center text-[#C9A227] group-hover:bg-[#C9A227] group-hover:text-neutral-900 transition-colors duration-300">
                      <ArrowUpRight size={18} strokeWidth={1.5} />
                    </span>
                  </div>
                </Link>
              )}
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-16" data-testid="watch-brands-marquee">
            <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-6">
              Watch brands we buy &amp; sell
            </p>
            <div className="relative overflow-hidden border-y border-[#E8DCC4] py-5 [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]">
              <div className="flex gap-14 w-max animate-marquee">
                {[...watchBrands, ...watchBrands].map((brand, i) => (
                  <span key={`${brand}-${i}`} className="font-display text-xl text-[#A8871B] whitespace-nowrap" data-testid={i < watchBrands.length ? `brand-${brand.toLowerCase().replace(/\s+/g, "-")}` : undefined}>
                    {brand}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="mt-10 text-sm text-neutral-500" data-testid="we-buy-note">
            Every offer is obligation-free. Valid photo ID required for all sales, as Texas law requires.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
